import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap,
  MapPin,
  Briefcase,
  DollarSign,
  TrendingUp,
  Lightbulb,
  Building2,
  Award,
  ExternalLink,
  ChevronLeft,
  Loader2,
  Sparkles,
  Target,
  BookOpen,
  Users,
  Globe,
  CheckCircle,
  ArrowRight,
  PiggyBank,
  Copy,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fun facts database organized by career category - 8 facts each for variety
const careerFunFacts = {
  tech: [
    { fact: "The first computer programmer was Ada Lovelace in the 1840s!", icon: "💻" },
    { fact: "Software developers have one of the highest job satisfaction rates at 72%", icon: "😊" },
    { fact: "The average tech worker changes jobs every 2.3 years", icon: "🔄" },
    { fact: "Remote work increased developer productivity by 47% according to studies", icon: "🏠" },
    { fact: "Canada's tech sector grew 3x faster than the overall economy", icon: "🇨🇦" },
    { fact: "The term 'bug' came from an actual moth found in a computer in 1947", icon: "🐛" },
    { fact: "Shopify alone created 10,000+ tech jobs in Canada since 2015", icon: "🛒" },
    { fact: "Toronto-Waterloo corridor is the 2nd fastest growing tech hub in North America", icon: "🚀" },
  ],
  healthcare: [
    { fact: "Healthcare is the fastest-growing employment sector in Canada", icon: "🏥" },
    { fact: "Nurses have been voted the most trusted profession for 20+ years", icon: "💉" },
    { fact: "Mental health professionals are in highest demand since 2020", icon: "🧠" },
    { fact: "Canada needs 60,000+ more healthcare workers by 2030", icon: "📈" },
    { fact: "Healthcare workers report the highest sense of purpose at work", icon: "❤️" },
    { fact: "Registered nurses can specialize in 20+ different fields", icon: "⭐" },
    { fact: "Healthcare benefits packages are worth $15-25k extra per year", icon: "💎" },
    { fact: "Travel nurses can earn 50-100% more than staff nurses", icon: "✈️" },
  ],
  finance: [
    { fact: "Toronto is the 2nd largest financial hub in North America", icon: "🏦" },
    { fact: "CPA designation holders earn 25% more on average", icon: "📊" },
    { fact: "Financial analysts have 95% job placement within 6 months", icon: "💼" },
    { fact: "Fintech jobs in Canada grew 50% in the last 3 years", icon: "🚀" },
    { fact: "The Big 5 banks employ over 400,000 Canadians", icon: "🇨🇦" },
    { fact: "CFAs earn an average of $150k+ in Canada", icon: "💰" },
    { fact: "Accounting is one of the most recession-proof careers", icon: "🛡️" },
    { fact: "Finance professionals get 15-25% bonuses on average", icon: "🎯" },
  ],
  trades: [
    { fact: "Skilled trades have a 99% employment rate in Canada!", icon: "🔧" },
    { fact: "Electricians can earn $100k+ in major cities", icon: "⚡" },
    { fact: "There's a shortage of 10,000+ plumbers in Canada", icon: "🔨" },
    { fact: "Red Seal certification is recognized across all provinces", icon: "🏅" },
    { fact: "Trade apprentices earn while they learn - no student debt!", icon: "💰" },
    { fact: "Average age of tradespeople is 55 - huge opportunity for youth!", icon: "📈" },
    { fact: "Welders working in oil & gas can earn $120k+", icon: "🔥" },
    { fact: "Owning a trades business can lead to $200k+ income", icon: "🏠" },
  ],
  creative: [
    { fact: "Canada's creative economy employs 850,000+ people", icon: "🎨" },
    { fact: "Vancouver is Hollywood North - 3rd largest film production hub", icon: "🎬" },
    { fact: "Graphic designers are needed in virtually every industry", icon: "✏️" },
    { fact: "UX/UI design salaries increased 35% in 5 years", icon: "📱" },
    { fact: "Creative professionals report highest work flexibility", icon: "🌟" },
    { fact: "Top hairstylists in Toronto can earn $80k+ with tips", icon: "💇" },
    { fact: "Freelance creatives can deduct 30%+ in business expenses", icon: "💵" },
    { fact: "Instagram helped barbers & stylists increase income by 40%", icon: "📸" },
  ],
  education: [
    { fact: "Teachers in Canada earn 20-40% more than US average", icon: "📚" },
    { fact: "Education sector has excellent pension and benefits", icon: "🎓" },
    { fact: "Ontario teachers are among the highest-paid globally", icon: "🍎" },
    { fact: "Teaching abroad can increase earning potential by 50%", icon: "🌍" },
    { fact: "Education has one of the best work-life balances", icon: "⚖️" },
    { fact: "Tutors can earn $50-150/hour for specialized subjects", icon: "💡" },
    { fact: "Teacher pensions are worth $1M+ over retirement", icon: "🏆" },
    { fact: "EdTech created 5,000+ new education jobs in Canada", icon: "💻" },
  ],
  hospitality: [
    { fact: "Tourism contributes $105 billion to Canada's economy", icon: "✈️" },
    { fact: "Hotel management grads have 90%+ employment rates", icon: "🏨" },
    { fact: "Chefs with Red Seal earn 30% more than uncertified", icon: "👨‍🍳" },
    { fact: "Hospitality managers can work anywhere in the world", icon: "🌎" },
    { fact: "Event planning is one of the fastest-growing careers", icon: "🎉" },
    { fact: "Top bartenders in Toronto can earn $70k+ with tips", icon: "🍸" },
    { fact: "Hotel GMs at major chains earn $120k-200k+", icon: "🏆" },
    { fact: "Cruise ship workers can save 90% of their salary", icon: "🚢" },
  ],
  legal: [
    { fact: "Lawyers in Canada earn 40% more than US average after taxes", icon: "⚖️" },
    { fact: "Paralegals are one of the fastest-growing legal careers", icon: "📋" },
    { fact: "Bay Street lawyers can earn $200k+ in first year", icon: "💰" },
    { fact: "Legal tech is revolutionizing how law is practiced", icon: "🤖" },
    { fact: "In-house counsel positions grew 65% in 5 years", icon: "📈" },
    { fact: "IP lawyers are among the highest paid legal specialists", icon: "💎" },
    { fact: "Legal aid lawyers report highest job satisfaction", icon: "❤️" },
    { fact: "Law firm partners can earn $500k-2M+ annually", icon: "🏆" },
  ],
  science: [
    { fact: "Canada invests $35 billion annually in R&D", icon: "🔬" },
    { fact: "Environmental scientists are in top 10 growing careers", icon: "🌱" },
    { fact: "Biotech sector salaries increased 40% since 2020", icon: "🧬" },
    { fact: "Research scientists can earn more in industry than academia", icon: "💡" },
    { fact: "Canada has 5 of the world's top 100 research universities", icon: "🏛️" },
    { fact: "Data scientists are called the 'sexiest job of the 21st century'", icon: "📊" },
    { fact: "Pharmaceutical scientists earn 25% more than average", icon: "💊" },
    { fact: "Climate scientists are in unprecedented demand globally", icon: "🌍" },
  ],
  general: [
    { fact: "The average Canadian changes careers 5-7 times in their lifetime", icon: "🔄" },
    { fact: "Bilingual employees earn 10-15% more on average", icon: "🗣️" },
    { fact: "Networking fills 85% of job positions before they're posted", icon: "🤝" },
    { fact: "LinkedIn users are 40x more likely to be recruited", icon: "💼" },
    { fact: "The first 5 years of your career shape the next 25", icon: "🚀" },
    { fact: "Negotiating salary can add $1M+ to lifetime earnings", icon: "💰" },
    { fact: "Canadians with a degree earn 40% more over their lifetime", icon: "🎓" },
    { fact: "Job hopping every 2-3 years can increase salary by 50%", icon: "📈" },
  ],
};

// Function to match job title to career category
const getCareerCategory = (jobTitle) => {
  const title = jobTitle.toLowerCase();
  
  // Tech keywords
  if (/software|developer|engineer|programmer|data|analyst|it |tech|cyber|cloud|devops|web|mobile|ai |machine learning|computer/i.test(title)) {
    return "tech";
  }
  // Healthcare keywords
  if (/nurse|doctor|physician|therapist|psychologist|psychiatrist|pharmacist|dental|medical|health|clinical|patient|hospital|care|physio|chiro|veterinar/i.test(title)) {
    return "healthcare";
  }
  // Finance keywords
  if (/accountant|finance|financial|banking|investment|cpa|cfa|auditor|tax|actuary|trader|analyst/i.test(title) && !/data analyst/i.test(title)) {
    return "finance";
  }
  // Trades keywords
  if (/electrician|plumber|carpenter|welder|mechanic|hvac|construction|contractor|technician|trade|mason|roofer|painter|landscap/i.test(title)) {
    return "trades";
  }
  // Creative keywords
  if (/design|artist|graphic|creative|photographer|video|film|animator|writer|content|media|market|ux|ui|brand/i.test(title)) {
    return "creative";
  }
  // Education keywords
  if (/teacher|professor|instructor|tutor|education|academic|principal|counselor|librarian/i.test(title)) {
    return "education";
  }
  // Hospitality keywords
  if (/chef|cook|hotel|restaurant|hospitality|tourism|travel|event|bartender|server|manager/i.test(title) && /hotel|restaurant|hospitality|food|beverage|culinary/i.test(title)) {
    return "hospitality";
  }
  // Legal keywords
  if (/lawyer|legal|attorney|paralegal|law|judge|solicitor|barrister/i.test(title)) {
    return "legal";
  }
  // Science keywords
  if (/scientist|researcher|biolog|chemist|physic|geolog|environment|lab|research/i.test(title)) {
    return "science";
  }
  
  return "general";
};

// Fun Facts Display Component
const FunFactsDisplay = ({ jobTitle }) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const category = getCareerCategory(jobTitle || "");
  const facts = careerFunFacts[category] || careerFunFacts.general;

  // Rotate through facts every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % facts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [facts.length]);

  const currentFact = facts[currentFactIndex];

  return (
    <motion.div
      key={currentFactIndex}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="mt-6 max-w-md mx-auto"
    >
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
        <p className="text-xs text-indigo-500 font-medium mb-1 uppercase tracking-wide">
          Did you know?
        </p>
        <p className="text-slate-700 flex items-center gap-2">
          <span className="text-2xl">{currentFact.icon}</span>
          <span className="text-sm">{currentFact.fact}</span>
        </p>
      </div>
      <div className="flex justify-center gap-1 mt-3">
        {facts.slice(0, 8).map((_, idx) => (
          <div
            key={idx}
            className={`w-2 h-2 rounded-full transition-colors ${
              idx === currentFactIndex ? "bg-indigo-500" : "bg-indigo-200"
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
};

// Loading Progress Component with animated stages
const LoadingProgress = () => {
  const [stage, setStage] = useState(0);
  const stages = [
    { text: "Searching salary databases...", icon: "🔍" },
    { text: "Analyzing market trends...", icon: "📊" },
    { text: "Finding top employers...", icon: "🏢" },
    { text: "Generating personalized tips...", icon: "💡" },
    { text: "Finalizing your report...", icon: "✨" },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setStage((prev) => (prev < stages.length - 1 ? prev + 1 : prev));
    }, 5000);
    return () => clearInterval(interval);
  }, [stages.length]);

  const progress = ((stage + 1) / stages.length) * 100;

  return (
    <div className="max-w-sm mx-auto mb-4">
      <div className="flex items-center justify-center gap-2 mb-2">
        <span className="text-lg">{stages[stage].icon}</span>
        <motion.p
          key={stage}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm text-slate-500"
        >
          {stages[stage].text}
        </motion.p>
      </div>
      <Progress value={progress} className="h-2" />
      <p className="text-xs text-slate-400 mt-2">This usually takes 20-30 seconds for accurate results</p>
    </div>
  );
};

const SalaryExplorer = ({ onBack }) => {
  const [step, setStep] = useState("form"); // form, loading, results
  const [loading, setLoading] = useState(false);
  const [salaryData, setSalaryData] = useState(null);
  const [streamProgress, setStreamProgress] = useState({ stage: '', message: '', progress: 0 });
  const [formData, setFormData] = useState({
    university: "",
    major: "",
    year_of_study: "",
    target_city: "",
    target_job_title: "",
    job_type: "full_time",
    years_experience: 0,
    work_authorization: "Canadian citizen",
  });

  const universities = [
    "University of Toronto",
    "University of British Columbia",
    "McGill University",
    "University of Waterloo",
    "McMaster University",
    "Western University",
    "Queen's University",
    "York University",
    "University of Alberta",
    "University of Calgary",
    "University of Ottawa",
    "Ryerson University (TMU)",
    "Simon Fraser University",
    "Carleton University",
    "Dalhousie University",
    "Other",
  ];

  const cities = [
    "Toronto, ON",
    "Vancouver, BC",
    "Montreal, QC",
    "Calgary, AB",
    "Ottawa, ON",
    "Edmonton, AB",
    "Mississauga, ON",
    "Winnipeg, MB",
    "Quebec City, QC",
    "Hamilton, ON",
    "Kitchener-Waterloo, ON",
    "Halifax, NS",
    "Remote (Canada)",
  ];

  const yearOptions = [
    { value: "1st", label: "1st Year" },
    { value: "2nd", label: "2nd Year" },
    { value: "3rd", label: "3rd Year" },
    { value: "4th", label: "4th Year" },
    { value: "recent_grad", label: "Recent Graduate" },
    { value: "1_year_post_grad", label: "1+ Year Post-Grad" },
  ];

  const jobTypes = [
    { value: "internship", label: "Internship", icon: BookOpen },
    { value: "co_op", label: "Co-op", icon: Users },
    { value: "part_time", label: "Part-Time", icon: Clock },
    { value: "full_time", label: "Full-Time", icon: Briefcase },
  ];

  const workAuthOptions = [
    "Canadian citizen",
    "Permanent resident",
    "International student with work permit",
  ];

  const handleSubmit = async () => {
    // Validation
    if (!formData.university || !formData.major || !formData.year_of_study || 
        !formData.target_city || !formData.target_job_title) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setStep("loading");
    setStreamProgress({ stage: 'starting', message: 'Initializing...', progress: 5 });

    try {
      // Use streaming endpoint for real-time updates
      const response = await fetch(`${API_URL}/api/salary-explorer/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              setStreamProgress({
                stage: data.stage,
                message: data.message,
                progress: data.progress
              });

              if (data.stage === 'complete' && data.data) {
                setSalaryData(data.data);
                setStep("results");
              } else if (data.stage === 'error') {
                throw new Error(data.message);
              }
            } catch (e) {
              // Ignore JSON parse errors for incomplete chunks
              if (e.message !== 'Unexpected end of JSON input') {
                console.error('Stream parse error:', e);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Salary explorer error:", error);
      toast.error("Failed to get salary insights. Please try again.");
      setStep("form");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResults = async () => {
    if (!salaryData) return;
    
    const sr = salaryData.salary_ranges;
    const text = `
💼 ${salaryData.profile_summary?.job_title || formData.target_job_title} - Salary Insights
📍 ${salaryData.profile_summary?.location || formData.target_city}

💰 SALARY RANGES (CAD)
━━━━━━━━━━━━━━━━━━━━
${salaryData.salary_ranges?.current_target?.type === "full_time_annual" ? 
  `Current Target: $${sr?.current_target?.low_cad?.toLocaleString()} - $${sr?.current_target?.high_cad?.toLocaleString()}/year` :
  `Current Target: $${sr?.current_target?.low_cad?.toLocaleString()} - $${sr?.current_target?.high_cad?.toLocaleString()}`}
Entry Level: $${sr?.entry_level?.low_cad?.toLocaleString()} - $${sr?.entry_level?.high_cad?.toLocaleString()}
Mid Level: $${sr?.mid_level?.low_cad?.toLocaleString()} - $${sr?.mid_level?.high_cad?.toLocaleString()}
Senior: $${sr?.senior?.low_cad?.toLocaleString()} - $${sr?.senior?.high_cad?.toLocaleString()}

🚀 TOP TIPS
${salaryData.tips_to_maximize_earnings?.slice(0, 3).map((tip, i) => 
  `${i + 1}. ${tip.title} (${tip.estimated_impact})`).join('\n')}

Made with Compoundr 🌱
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Results copied!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const formatCurrency = (amount) => {
    return `$${amount?.toLocaleString() || 0}`;
  };

  return (
    <div className="space-y-6" data-testid="salary-explorer">
      {/* Back button */}
      {onBack && (
        <Button
          variant="ghost"
          onClick={() => window.location.href = '/tools'}
          className="text-slate-600 hover:text-navy-700"
          data-testid="back-to-plan-btn"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to All Tools
        </Button>
      )}

      {/* Header */}
      <div className="text-center">
        <Badge className="bg-green-100 text-green-700 mb-3">Career Tool</Badge>
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-navy-700 mb-2">
          Know Your Numbers
        </h2>
        <p className="text-lg text-emerald-600 font-medium mb-1">Salary Explorer</p>
        <p className="text-slate-600 text-sm">
          See what you could earn after graduation based on your field, location, and experience
        </p>
      </div>

      <AnimatePresence mode="wait">
        {/* Form Step */}
        {step === "form" && (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Card className="border-0 shadow-floating">
              <CardContent className="pt-6 space-y-6">
                {/* University & Major */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-emerald-600" />
                      University *
                    </Label>
                    <Select
                      value={formData.university}
                      onValueChange={(v) => setFormData({ ...formData, university: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select university" />
                      </SelectTrigger>
                      <SelectContent>
                        {universities.map((uni) => (
                          <SelectItem key={uni} value={uni}>{uni}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-emerald-600" />
                      Major / Program *
                    </Label>
                    <Input
                      placeholder="e.g., Computer Science, Business"
                      value={formData.major}
                      onChange={(e) => setFormData({ ...formData, major: e.target.value })}
                    />
                  </div>
                </div>

                {/* Year & City */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Year of Study *</Label>
                    <Select
                      value={formData.year_of_study}
                      onValueChange={(v) => setFormData({ ...formData, year_of_study: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-emerald-600" />
                      Target City *
                    </Label>
                    <Select
                      value={formData.target_city}
                      onValueChange={(v) => setFormData({ ...formData, target_city: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Where do you want to work?" />
                      </SelectTrigger>
                      <SelectContent>
                        {cities.map((city) => (
                          <SelectItem key={city} value={city}>{city}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Target Job */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-emerald-600" />
                    Target Job Title *
                  </Label>
                  <Input
                    placeholder="e.g., Software Engineer, Marketing Manager, Accountant"
                    value={formData.target_job_title}
                    onChange={(e) => setFormData({ ...formData, target_job_title: e.target.value })}
                  />
                </div>

                {/* Job Type */}
                <div className="space-y-3">
                  <Label>What type of position are you looking for?</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {jobTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setFormData({ ...formData, job_type: type.value })}
                        className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${
                          formData.job_type === type.value
                            ? "border-emerald-500 bg-emerald-50"
                            : "border-slate-200 hover:border-emerald-300"
                        }`}
                      >
                        <type.icon className="w-5 h-5" />
                        <span className="text-sm">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Experience & Work Auth */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Years of Relevant Experience</Label>
                    <Select
                      value={String(formData.years_experience)}
                      onValueChange={(v) => setFormData({ ...formData, years_experience: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((yr) => (
                          <SelectItem key={yr} value={String(yr)}>
                            {yr === 0 ? "No experience yet" : `${yr} year${yr > 1 ? "s" : ""}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-emerald-600" />
                      Work Authorization
                    </Label>
                    <Select
                      value={formData.work_authorization}
                      onValueChange={(v) => setFormData({ ...formData, work_authorization: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {workAuthOptions.map((opt) => (
                          <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
                  data-testid="explore-salary-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Explore My Salary Potential
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Loading Step with Fun Facts */}
        {step === "loading" && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-8"
          >
            <div className="w-20 h-20 mx-auto mb-6 relative">
              <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping" />
              <div className="relative w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center">
                <DollarSign className="w-10 h-10 text-white" />
              </div>
            </div>
            <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">
              Researching Salary Data...
            </h3>
            <p className="text-slate-600 mb-4">
              Analyzing market data for <span className="font-semibold text-emerald-600">{formData.target_job_title}</span> in {formData.target_city}
            </p>
            
            {/* Real-time streaming progress */}
            <div className="max-w-sm mx-auto mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-lg">
                  {streamProgress.stage === 'starting' && '🚀'}
                  {streamProgress.stage === 'researching' && '🔍'}
                  {streamProgress.stage === 'analyzing' && '📊'}
                  {streamProgress.stage === 'processing' && '⚙️'}
                  {streamProgress.stage === 'finalizing' && '✨'}
                </span>
                <motion.p
                  key={streamProgress.message}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-sm text-slate-500"
                >
                  {streamProgress.message || 'Initializing...'}
                </motion.p>
              </div>
              <Progress value={streamProgress.progress} className="h-2" />
              <p className="text-xs text-slate-400 mt-2">Powered by real-time market data</p>
            </div>
            
            {/* Fun Facts Section */}
            <FunFactsDisplay jobTitle={formData.target_job_title} />
          </motion.div>
        )}

        {/* Results Step */}
        {step === "results" && salaryData && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Key Message */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 text-white text-center">
              <PiggyBank className="w-10 h-10 mx-auto mb-3 opacity-90" />
              <p className="text-lg font-medium">{salaryData.key_message}</p>
            </div>

            {/* Profile Summary */}
            <Card className="border-0 shadow-floating">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-bold text-navy-700">
                      {salaryData.profile_summary?.job_title || formData.target_job_title}
                    </h3>
                    <p className="text-slate-600 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {salaryData.profile_summary?.location || formData.target_city}
                      <Badge variant="outline" className="ml-2 text-xs">
                        {formData.job_type.replace("_", " ")}
                      </Badge>
                    </p>
                  </div>
                </div>

                {/* University Context */}
                {salaryData.profile_summary?.university_context && (
                  <div className="bg-blue-50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-700 flex items-start gap-2">
                      <GraduationCap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {salaryData.profile_summary.university_context}
                    </p>
                  </div>
                )}

                {/* Current Target Salary */}
                {salaryData.salary_ranges?.current_target && (
                  <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                    <p className="text-sm text-emerald-600 mb-1">
                      Your Target ({salaryData.salary_ranges.current_target.type?.replace(/_/g, " ")})
                    </p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-emerald-700">
                        {formatCurrency(salaryData.salary_ranges.current_target.low_cad)}
                      </span>
                      <span className="text-slate-500">to</span>
                      <span className="text-3xl font-bold text-emerald-700">
                        {formatCurrency(salaryData.salary_ranges.current_target.high_cad)}
                      </span>
                    </div>
                    {salaryData.salary_ranges.current_target.note && (
                      <p className="text-sm text-slate-600 mt-2">
                        {salaryData.salary_ranges.current_target.note}
                      </p>
                    )}
                  </div>
                )}

                {/* Salary Ranges by Experience */}
                <h4 className="font-semibold text-navy-700 mb-3">Salary Growth Path</h4>
                <div className="grid grid-cols-3 gap-3">
                  {["entry_level", "mid_level", "senior"].map((level) => {
                    const data = salaryData.salary_ranges?.[level];
                    if (!data) return null;
                    const labels = {
                      entry_level: { title: "Entry Level", subtitle: "0-2 years", color: "blue" },
                      mid_level: { title: "Mid Level", subtitle: "3-5 years", color: "purple" },
                      senior: { title: "Senior", subtitle: "5+ years", color: "amber" },
                    };
                    const label = labels[level];
                    return (
                      <div key={level} className={`bg-${label.color}-50 rounded-xl p-3 text-center`}>
                        <p className={`text-xs font-medium text-${label.color}-600 mb-1`}>{label.title}</p>
                        <p className="text-sm font-bold text-navy-700">
                          {formatCurrency(data.low_cad)} - {formatCurrency(data.high_cad)}
                        </p>
                        <p className="text-xs text-slate-500">{label.subtitle}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Personalized Advice Section */}
            {salaryData.personalized_advice && (
              <Card className="border-0 shadow-floating bg-gradient-to-br from-indigo-50 to-purple-50">
                <CardContent className="pt-6">
                  <h4 className="font-heading font-bold text-navy-700 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-500" />
                    Personalized Advice For You
                  </h4>
                  <div className="space-y-4">
                    {salaryData.personalized_advice.based_on_experience && (
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-xs font-semibold text-indigo-600 mb-1 uppercase tracking-wide">Based on Your Experience</p>
                        <p className="text-sm text-slate-700">{salaryData.personalized_advice.based_on_experience}</p>
                      </div>
                    )}
                    {salaryData.personalized_advice.based_on_university && (
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-xs font-semibold text-purple-600 mb-1 uppercase tracking-wide">Leveraging Your University</p>
                        <p className="text-sm text-slate-700">{salaryData.personalized_advice.based_on_university}</p>
                      </div>
                    )}
                    {salaryData.personalized_advice.based_on_career && (
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-xs font-semibold text-emerald-600 mb-1 uppercase tracking-wide">Industry-Specific Insights</p>
                        <p className="text-sm text-slate-700">{salaryData.personalized_advice.based_on_career}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tips to Maximize Earnings */}
            {salaryData.tips_to_maximize_earnings?.length > 0 && (
              <Card className="border-0 shadow-floating">
                <CardContent className="pt-6">
                  <h4 className="font-heading font-bold text-navy-700 mb-4 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-500" />
                    Tips to Boost Your Salary
                  </h4>
                  <div className="space-y-4">
                    {salaryData.tips_to_maximize_earnings?.map((tip, idx) => (
                      <div key={idx} className="flex gap-3 p-3 bg-slate-50 rounded-xl">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-bold text-sm">
                          {tip.rank || idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <p className="font-semibold text-navy-700">{tip.title}</p>
                            {tip.estimated_impact && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                {tip.estimated_impact}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mb-2">{tip.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {tip.difficulty && (
                              <Badge variant="outline" className="text-xs">
                                {tip.difficulty}
                              </Badge>
                            )}
                            {tip.timeline && (
                              <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                                <Clock className="w-3 h-3 mr-1" />
                                {tip.timeline}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* In-Demand Skills */}
            {salaryData.in_demand_skills?.length > 0 && (
              <Card className="border-0 shadow-floating">
                <CardContent className="pt-6">
                  <h4 className="font-heading font-bold text-navy-700 mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-500" />
                    In-Demand Skills with Salary Premiums
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {salaryData.in_demand_skills?.map((skill, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-navy-700">{skill.skill}</p>
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            {skill.premium}
                          </Badge>
                        </div>
                        {skill.relevance && (
                          <p className="text-xs text-slate-600 mb-2">{skill.relevance}</p>
                        )}
                        {skill.how_to_learn && (
                          <p className="text-xs text-blue-600">
                            <BookOpen className="w-3 h-3 inline mr-1" />
                            {skill.how_to_learn}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Top Hiring Companies - Enhanced */}
            {(salaryData.top_hiring_companies?.length > 0 || salaryData.top_paying_companies?.length > 0) && (
              <Card className="border-0 shadow-floating">
                <CardContent className="pt-6">
                  <h4 className="font-heading font-bold text-navy-700 mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Top Companies Hiring for This Role
                  </h4>
                  {salaryData.top_hiring_companies?.length > 0 ? (
                    <div className="space-y-3">
                      {salaryData.top_hiring_companies.map((company, idx) => (
                        <div key={idx} className="bg-slate-50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-navy-700">{company.name}</p>
                            {company.typical_salary_range && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                {company.typical_salary_range}
                              </Badge>
                            )}
                          </div>
                          {company.why_good_fit && (
                            <p className="text-sm text-slate-600 mb-1">{company.why_good_fit}</p>
                          )}
                          {company.known_for && (
                            <p className="text-xs text-blue-600">
                              <CheckCircle className="w-3 h-3 inline mr-1" />
                              Known for: {company.known_for}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {salaryData.top_paying_companies?.map((company, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-slate-700 p-2 bg-slate-50 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          {company}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Investment Potential */}
            {salaryData.investing_opportunity && (
              <Card className="border-0 shadow-floating bg-gradient-to-br from-emerald-50 to-teal-50">
                <CardContent className="pt-6">
                  <h4 className="font-heading font-bold text-navy-700 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    Investment Potential
                  </h4>
                  <p className="text-sm text-slate-700 mb-2">{salaryData.investing_opportunity.message}</p>
                  {salaryData.investing_opportunity.annual_savings_potential && (
                    <p className="text-sm text-emerald-700 font-medium">
                      {salaryData.investing_opportunity.annual_savings_potential}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Market Context */}
            {salaryData.market_context && (
              <div className="bg-blue-50 rounded-xl p-4">
                <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Market Outlook
                </h4>
                <p className="text-sm text-slate-700">{salaryData.market_context}</p>
              </div>
            )}

            {/* Work Authorization Note */}
            {salaryData.work_authorization_note && (
              <div className="bg-amber-50 rounded-xl p-4">
                <p className="text-sm text-amber-700">
                  <Globe className="w-4 h-4 inline mr-1" />
                  {salaryData.work_authorization_note}
                </p>
              </div>
            )}

            {/* Verification Links */}
            {salaryData.verification_links && (
              <div className="bg-slate-50 rounded-xl p-4">
                <h4 className="font-semibold text-slate-700 mb-3 text-sm">Verify Salaries & Find Jobs</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {salaryData.verification_links.glassdoor && (
                    <a
                      href={salaryData.verification_links.glassdoor}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-sm text-green-600 hover:text-green-800 bg-white px-3 py-2 rounded-lg border border-green-100 hover:border-green-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Glassdoor
                    </a>
                  )}
                  {salaryData.verification_links.linkedin_jobs && (
                    <a
                      href={salaryData.verification_links.linkedin_jobs}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-800 bg-white px-3 py-2 rounded-lg border border-blue-100 hover:border-blue-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      LinkedIn Jobs
                    </a>
                  )}
                  {salaryData.verification_links.linkedin_salary && (
                    <a
                      href={salaryData.verification_links.linkedin_salary}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-800 bg-white px-3 py-2 rounded-lg border border-blue-100 hover:border-blue-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      LinkedIn Salaries
                    </a>
                  )}
                  {salaryData.verification_links.indeed && (
                    <a
                      href={salaryData.verification_links.indeed}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-sm text-purple-600 hover:text-purple-800 bg-white px-3 py-2 rounded-lg border border-purple-100 hover:border-purple-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Indeed
                    </a>
                  )}
                  {salaryData.verification_links.payscale && (
                    <a
                      href={salaryData.verification_links.payscale}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-sm text-orange-600 hover:text-orange-800 bg-white px-3 py-2 rounded-lg border border-orange-100 hover:border-orange-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      PayScale
                    </a>
                  )}
                  {salaryData.verification_links.job_bank && (
                    <a
                      href={salaryData.verification_links.job_bank}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 text-sm text-red-600 hover:text-red-800 bg-white px-3 py-2 rounded-lg border border-red-100 hover:border-red-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Job Bank
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Accuracy Disclaimer - Enhanced */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 mb-2 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Important Note on Accuracy
              </h4>
              <p className="text-xs text-amber-700">
                {salaryData.accuracy_disclaimer || salaryData.disclaimer || 
                  "These salary ranges are well-researched estimates based on current market data from Glassdoor, Statistics Canada, PayScale, and industry reports. Actual salaries may fluctuate based on company size, your negotiation skills, specific qualifications, economic conditions, and individual circumstances. Always verify with multiple sources and current job postings."}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 justify-center pt-4">
              <Button onClick={handleCopyResults} variant="outline" className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Copy Results
              </Button>
              <Button
                onClick={() => {
                  setStep("form");
                  setSalaryData(null);
                }}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Explore Another Role
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SalaryExplorer;
